/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * Sparkline — tiny dependency-free SVG mini chart.
 *
 * Used in tabular contexts (LDView's per-precinct grid, the heatmap's
 * row labels, etc.) where a full Recharts container would be wasteful.
 * Renders a single polyline + optional area fill + last-point dot in
 * a fixed-aspect SVG. The component is purely presentational — pass
 * an array of numbers and an optional formatter for the tooltip
 * `<title>` element.
 *
 * Width / height defaults are tuned for inline use inside a 32-px-tall
 * table row. Pass explicit dimensions for other contexts.
 */
import React from 'react';

type Props = {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  showDot?: boolean;
  /** Optional formatter used for the SVG <title> hover label. */
  format?: (v: number, i: number) => string;
  /** Optional X-axis labels (ISO dates) used in the hover label. */
  labels?: string[];
  className?: string;
  /**
   * Optional screen-reader label override. When provided, replaces the
   * generic "Sparkline of N values" auto-label. Caller is expected to
   * summarize the trend in plain English ("Trend: up 12% over 6 elections").
   */
  ariaLabel?: string;
};

export function Sparkline({
  values,
  width = 96,
  height = 28,
  stroke = '#dc2626',
  fill = 'rgba(220, 38, 38, 0.18)',
  showDot = true,
  format,
  labels,
  className,
  ariaLabel,
}: Props) {
  if (!values || values.length === 0) {
    return (
      <svg
        width={width}
        height={height}
        className={className}
        aria-label="No data"
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="#475569"
          strokeDasharray="2 3"
        />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const padX = 2;
  const padY = 2;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const stepX = values.length > 1 ? innerW / (values.length - 1) : 0;

  const pts = values.map((v, i) => {
    const x = padX + stepX * i;
    const y = padY + innerH - ((v - min) / range) * innerH;
    return [x, y] as [number, number];
  });

  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath =
    `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${(padY + innerH).toFixed(1)} L${pts[0][0].toFixed(1)},${(padY + innerH).toFixed(1)} Z`;

  const last = pts[pts.length - 1];
  const titleLines = values
    .map((v, i) => {
      const formatted = format ? format(v, i) : v.toLocaleString();
      const label = labels?.[i];
      return label ? `${label}: ${formatted}` : formatted;
    })
    .join('\n');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={
        ariaLabel ??
        (() => {
          const first = values[0];
          const last = values[values.length - 1];
          const delta = last - first;
          const dir =
            delta > 0 ? 'trending up' : delta < 0 ? 'trending down' : 'flat';
          const pct = first !== 0 ? ((delta / Math.abs(first)) * 100).toFixed(0) : '0';
          return `Sparkline: ${values.length} data points, ${dir} ${Math.abs(Number(pct))}% from ${first.toLocaleString()} to ${last.toLocaleString()}.`;
        })()
      }
    >
      <title>{titleLines}</title>
      <path d={areaPath} fill={fill} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDot && (
        <circle cx={last[0]} cy={last[1]} r={2} fill={stroke} />
      )}
    </svg>
  );
}

export default Sparkline;
